import { Link } from 'react-router-dom';
import { ShoppingBag, ArrowLeft } from 'lucide-react';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center gap-2">
              <ShoppingBag className="h-8 w-8 text-slate-900" />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Formula Library</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Back Button */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <h1 className="text-4xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="refund-title">
          Refund & Cancellation Policy
        </h1>
        
        <div className="prose prose-slate max-w-none">
          <p className="text-slate-600 mb-6">
            <strong>Last Updated:</strong> July 2026
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 font-medium">
              Please read this policy carefully before making a purchase. Due to the digital nature of our products, we have specific guidelines for refunds and cancellations.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              1. Digital Product Policy
            </h2>
            <p className="text-slate-600 mb-4">
              All products sold on Formula Library are digital goods that are delivered instantly via email upon successful payment. Due to the immediate delivery and non-returnable nature of digital products:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Once a digital product has been delivered to your email, it cannot be "returned"</li>
              <li>We encourage you to read product descriptions carefully before purchasing</li>
              <li>Contact us with any questions before completing your purchase</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              2. Refund Eligibility
            </h2>
            <p className="text-slate-600 mb-4">
              We offer refunds under the following circumstances:
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-4">
              <h3 className="font-bold text-green-800 mb-2">Eligible for Refund:</h3>
              <ul className="list-disc pl-6 text-green-700 space-y-2">
                <li><strong>Non-delivery:</strong> Product was not delivered within 24 hours of purchase</li>
                <li><strong>Technical issues:</strong> File is corrupted, unreadable, or incomplete</li>
                <li><strong>Duplicate purchase:</strong> You accidentally purchased the same product twice</li>
                <li><strong>Significantly different from description:</strong> Product content substantially differs from what was advertised</li>
              </ul>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <h3 className="font-bold text-red-800 mb-2">NOT Eligible for Refund:</h3>
              <ul className="list-disc pl-6 text-red-700 space-y-2">
                <li>Change of mind after accessing the product</li>
                <li>Dissatisfaction with business results (outcomes vary by implementation)</li>
                <li>Lack of understanding of the content (support is available)</li>
                <li>Already accessed or downloaded the product</li>
                <li>Purchased the wrong product without attempting to contact support first</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              3. Refund Request Process
            </h2>
            <p className="text-slate-600 mb-4">
              To request a refund:
            </p>
            <ol className="list-decimal pl-6 text-slate-600 mb-4 space-y-3">
              <li>
                <strong>Contact us within 7 days</strong> of your purchase via WhatsApp at +27 730 758 123
              </li>
              <li>
                <strong>Provide the following information:</strong>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Your order/payment ID</li>
                  <li>Email address used for purchase</li>
                  <li>Product name</li>
                  <li>Reason for refund request</li>
                  <li>Any supporting documentation (screenshots of issues, etc.)</li>
                </ul>
              </li>
              <li>
                <strong>Wait for our response</strong> - We review all refund requests within 2-3 business days
              </li>
            </ol>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              4. Refund Timeline
            </h2>
            <p className="text-slate-600 mb-4">
              If your refund is approved:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Processing time:</strong> 2-3 business days to review and approve</li>
              <li><strong>Refund method:</strong> Original payment method used for purchase</li>
              <li><strong>Bank processing:</strong> 5-10 business days for the refund to appear in your account</li>
              <li><strong>Currency:</strong> Refunds are issued in the same currency as the original transaction</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              5. Subscription Cancellation
            </h2>
            <p className="text-slate-600 mb-4">
              For subscription plans:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Monthly subscriptions:</strong> Cancel anytime before the next billing cycle</li>
              <li><strong>Yearly subscriptions:</strong> Prorated refunds may be available within the first 30 days</li>
              <li><strong>Access continues:</strong> You retain access until the end of your paid period</li>
              <li><strong>No partial refunds:</strong> After 30 days, yearly subscriptions are non-refundable</li>
            </ul>
            <p className="text-slate-600">
              To cancel your subscription, contact us via WhatsApp at +27 730 758 123.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              6. Chargebacks
            </h2>
            <p className="text-slate-600 mb-4">
              We strongly discourage initiating chargebacks with your bank or credit card company before contacting us directly. Filing a chargeback:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>May result in immediate account suspension</li>
              <li>Takes longer to resolve than working with us directly</li>
              <li>May affect your ability to make future purchases</li>
            </ul>
            <p className="text-slate-600">
              Please give us the opportunity to resolve any issues first. We are committed to customer satisfaction.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              7. Exchanges
            </h2>
            <p className="text-slate-600 mb-4">
              If you purchased the wrong product, we may offer an exchange for a product of equal or lesser value. Contact us within 48 hours of purchase to request an exchange.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              8. Customer Support
            </h2>
            <p className="text-slate-600 mb-4">
              Before requesting a refund, we encourage you to contact our support team. Many issues can be resolved without needing a refund:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Technical assistance with accessing your products</li>
              <li>Clarification on product content</li>
              <li>Re-sending download links</li>
              <li>Guidance on using the formulas</li>
            </ul>
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-6 mt-4">
              <h3 className="font-bold text-sky-800 mb-2">Contact Us</h3>
              <p className="text-sky-700">
                <strong>WhatsApp:</strong> +27 730 758 123<br />
                <strong>Available:</strong> 24/7 Support
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              9. Policy Updates
            </h2>
            <p className="text-slate-600">
              We reserve the right to modify this refund policy at any time. Changes will be posted on this page with an updated "Last Updated" date. Purchases made before policy changes will be subject to the policy in effect at the time of purchase.
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400">
            © 2026 Formula Library. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/terms" className="text-slate-400 hover:text-white transition-colors">Terms & Conditions</Link>
            <Link to="/refund-policy" className="text-slate-400 hover:text-white transition-colors">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RefundPolicy;
